import { useEffect, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { getQueryParams } from '../../utils/location'

export default function Form (props = {
    className: null,
    customValidations: null,
    disabled: null,
    enableOnChange: false,
    enableOnChangeSyncHistoryPop: false,
    enableOnChangeSyncHistoryPush: false,
    formRef: null,
    onSubmit: null,
    onStateUpdate: null
}) {
    const {
        customValidations,
        disabled,
        enableOnChange,
        enableOnChangeSyncHistoryPop,
        enableOnChangeSyncHistoryPush,
        formRef,
        onSubmit,
        onStateUpdate,
        ...renderProps
    } = props

    const form = useRef()

    const history = useHistory()

    const [state, setState] = useState({
        oldValue: null,
        newValue: null
    })

    const onRefCallback = (ref) => {
        form.current = ref

        if (formRef
                && typeof formRef === 'function')
            formRef(ref)
    }

    const getCustomValidationById = (id) => id
        && customValidations
        && Array.isArray(customValidations)
        && customValidations
            .filter(obj => obj
                && obj.id
                && obj.id === id
                && typeof obj.valid
                    === 'function')[0]

    const getFormValueObj = () => {
        if (!form.current)
            return {}

        const formData = new window.FormData(form.current)

        const formDataObj = Array.from(formData.entries())
            .reduce((acc, curr) => {
                const selector = `[name="${curr[0]}"]`

                const { type } = form.current
                    .querySelector(selector) || { type: null }

                const isCheckboxArray = type === 'checkbox'
                    && Array.from(form.current.querySelectorAll(selector))
                        .length > 1

                if (isCheckboxArray
                        && !acc[`${curr[0]}`])
                    acc[`${curr[0]}`] = []

                if (acc[`${curr[0]}`]) {
                    const isArray = Array.isArray(acc[`${curr[0]}`])

                    if (isArray)
                        acc[`${curr[0]}`].push(curr[1])

                    if (!isArray)
                        acc[`${curr[0]}`] = [acc[`${curr[0]}`], curr[1]]

                    return acc
                }

                acc[`${curr[0]}`] = typeof curr[1] === 'string'
                        && (curr[1] || '').match(/^(true|on)$/)
                    ? Boolean(curr[1])
                    : (typeof curr[1] === 'string'
                        ? curr[1].trim()
                        : curr[1])

                return acc
            }, {})

        return formDataObj
    }

    const getFormValuesStr = () => JSON.stringify(getFormValueObj())

    const getFormEls = () => Array.from(form.current
        ? form.current.querySelectorAll('input, textarea, select')
        : [])

    const getFormValidationEls = () => getFormEls()
        .filter(el => el.type !== 'hidden'
            && (el.pattern
                || el.required
                || el.max
                || el.maxlength
                || el.min
                || el.minlength
                || getCustomValidationById(el.id)))

    const getFormFieldParentEl = (el) => {
        let field = null

        if (!el)
            return field

        while (el !== document.body) {
            if (el.classList.contains('form-field')
                    || el.classList.contains('form-field-floating')) {
                field = el
                break
            }

            el = el.parentElement
        }

        return field
    }

    const isFormValid = () => !getFormValidationEls()
        .some(el => !el.validity.valid)

    const isDisabled = () => {
        if (!enableOnChange)
            return disabled

        return disabled
            || state.oldValue === state.newValue
            || (!isFormValid()
                && Boolean(form.current.querySelector('.invalid.dirty')))
    }

    const setDisabledState = () => {
        if (!enableOnChange
                || !form.current)
            return

        form.current
            .setAttribute('data-disabled', isDisabled()
                ? 'true'
                : 'false')

        Array.from(form.current.querySelectorAll('[type=submit]'))
            .forEach(el => (el.disabled = isDisabled()))
    }

    const onInputValidate = (e) => {
        if (!form.current)
            return

        const updateState = () => enableOnChange
            && setState({
                oldValue: state.oldValue,
                newValue: getFormValuesStr()
            })

        if (e.target.type === 'url'
                && e.type === 'blur'
                && e.target.getAttribute('data-url-scheme')
                && e.target.value) {
            const urlScheme = e.target.getAttribute('data-url-scheme')

            const urlSchemeExp = new RegExp(`^${urlScheme}:\\/\\/`)

            if (!urlScheme.match(urlSchemeExp))
                e.target.value = `${urlScheme}://${e.target.value
                    .replace(/^([a-zA-Z]*:\/\/)?/, '')}`
        }

        const customValidation = getCustomValidationById(e.target.id)

        if (!(e.target.required
                || e.target.pattern
                || e.target.max
                || e.target.maxlength
                || e.target.min
                || e.target.minlength
                || customValidation))
            return updateState()

        const field = getFormFieldParentEl(e.target) || {}

        const isFieldset = field.type === 'fieldset'

        const isRadioGroup = (isFieldset
                || field.role === 'radiogroup')
            && field.querySelector('[type=radio], [role=radio]')

        if (isRadioGroup
                && e.target.validity.valid
                && field.classList.contains('invalid'))
            field.classList.remove('invalid')

        if (isRadioGroup
                && !e.target.validity.valid
                && !field.classList.contains('invalid'))
            field.classList.add('invalid')

        const isCheckboxRequiredGroup = isFieldset
            && field.querySelector('[type=checkbox]:required')

        const checkedBox = isCheckboxRequiredGroup
            && field.querySelector('[type=checkbox]:checked')

        if (isCheckboxRequiredGroup
                && checkedBox
                && field.classList.contains('invalid'))
            field.classList.remove('invalid')

        if (isCheckboxRequiredGroup
                && !checkedBox
                && !field.classList.contains('invalid'))
            field.classList.add('invalid')

        const invalid = field
            .querySelector('[id][role=alert]')

        if (customValidation) {
            const invalidDefaultAttr = 'data-invalid-default'

            const isCustomValid = customValidation.valid(e)

            const { message } = customValidation

            if (message
                    && invalid
                    && !invalid.getAttribute(invalidDefaultAttr))
                invalid.setAttribute(invalidDefaultAttr,
                    invalid.innerHTML)

            e.target
                .setCustomValidity(!isCustomValid
                    ? message || 'Error'
                    : '')

            const isInvalidReset = isCustomValid
                && invalid
                && invalid.innerHTML
                    !== invalid.getAttribute(invalidDefaultAttr)

            const isInvalidCustom = !isCustomValid
                && message
                && invalid
                && invalid.innerHTML !== message

            if (isInvalidReset
                    || isInvalidCustom)
                invalid.innerHTML = isInvalidReset
                    ? invalid.getAttribute(invalidDefaultAttr)
                    : message
        }

        updateState()

        if (!invalid)
            return

        const describedby = e.target.getAttribute('aria-describedby')

        if (e.target.validity.valid
                && describedby)
            return describedby.match(/\s/)
                ? e.target.setAttribute('aria-describedby',
                    describedby.replace(invalid.id, ''))
                : e.target.removeAttribute('aria-describedby')

        if (!e.target.validity.valid
                && (describedby || '').indexOf(invalid.id) === -1
                && e.target.classList.contains('dirty'))
            e.target.setAttribute('aria-describedby',
                `${describedby || ''} ${invalid.id}`.trim())
    }

    const onInputDirty = (e, forceDirty = false) => {
        if ((forceDirty
                    || e.target.value)
                && !e.target.classList.contains('dirty')) {
            e.target.classList.add('dirty')

            const field = getFormFieldParentEl(e.target)

            if (field
                    && !field.classList.contains('dirty'))
                field.classList.add('dirty')
        }

        onInputValidate(e)
    }

    const onSubmitHandler = (e) => {
        e.preventDefault()

        if (e.target.classList.contains('submitting')
                || e.target.getAttribute('data-disabled')
                    === 'true')
            return

        if (!isFormValid()) {
            getFormValidationEls()
                .forEach(el =>
                    onInputDirty({ target: el }, true))

            const focusEl = getFormValidationEls()
                .filter(el => !el.validity.valid)[0]

            if (focusEl)
                focusEl.focus()

            return
        }

        e.target.classList.add('submitting')

        Promise
            .resolve(typeof onSubmit === 'function'
                ? onSubmit(e)
                : form.submit())
            .then((success) => {
                e.target.classList.remove('submitting')

                if (!enableOnChange
                        || !success)
                    return

                setState({
                    oldValue: getFormValuesStr(),
                    newValue: getFormValuesStr()
                })
            })
    }

    const setFormEventListeners = () => {
        const isInit = form.current
            && form.current.getAttribute('data-init')
                !== 'false'

        const validationEls = getFormValidationEls()

        getFormEls()
            .forEach(el => {
                const isValidationEl = validationEls.indexOf(el) >= 0

                if (isInit
                        && el.type !== 'hidden'
                        && el.type !== 'radio'
                        && el.type !== 'checkbox'
                        && (el.value
                            || el.checked))
                    onInputDirty({ target: el }, true)

                if (!(isValidationEl
                        || enableOnChange))
                    return

                el.oninvalid = onInputValidate
                el.onkeydown = onInputValidate
                el.onkeyup = onInputValidate
                el.onchange = onInputValidate
                el.onblur = onInputDirty
            })

        if (isInit)
            form.current.setAttribute('data-init', false)
    }

    useEffect(() => {
        if (!form.current)
            return

        setFormEventListeners()

        if (enableOnChange
                || !state.oldValue)
            setState({
                oldValue: getFormValuesStr(),
                newValue: getFormValuesStr()
            })
    }, [form.current])

    useEffect(() => {
        if (!state.oldValue)
            return

        if (enableOnChange) {
            setFormEventListeners()
            setDisabledState()
        }

        if (onStateUpdate
                && typeof onStateUpdate === 'function')
            onStateUpdate(state)
    }, [state])

    useEffect(() => {
        const isSyncPop = enableOnChange
            && enableOnChangeSyncHistoryPop
            && history.action === 'POP'

        const isSyncPush = enableOnChange
            && enableOnChangeSyncHistoryPop
            && history.action === 'PUSH'

        if (!(isSyncPop || isSyncPush)
                || !state.oldValue)
            return

        const newState = {}

        const activeParams = getQueryParams(history.location.search)

        const formValues = getFormValueObj()

        Object.keys(formValues)
            .forEach(key => {
                const activeValue = activeParams[key] || ''

                const inputs = Array.from(form.current
                    .querySelectorAll(`[name=${key}]`))

                if (!activeValue
                        && inputs.length > 1
                        && inputs[0].type === 'checkbox')
                    return

                newState[key] = activeParams[key] || ''
            })

        Object.keys(activeParams)
            .forEach(key => {
                if (key in newState)
                    return

                newState[key] = activeParams[key] || ''
            })

        const newStateStr = JSON.stringify(newState)

        if (newStateStr
                === JSON.stringify(formValues))
            return

        const checkboxes = Array.from(form.current
            .querySelectorAll('[type=checkbox][name][value]'))

        checkboxes
            .forEach((el) => {
                if ((el.checked
                            && (newState[el.name] || [])
                                .indexOf(el.value) < 0)
                        || (!el.checked
                            && (newState[el.name] || [])
                                .indexOf(el.value) >= 0))
                    el.checked = !el.checked
            })

        setState({
            oldValue: newStateStr,
            newValue: newStateStr
        })
    }, [history.location.pathname, history.location.search])

    return (
        <form
            ref={onRefCallback}
            onSubmit={onSubmitHandler}
            noValidate
            data-disabled={isDisabled()}
            {...renderProps}
        >
            {props.children}
        </form>
    )
}
